<template>
  <div class="video-upload-container">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2>视频断点续传上传</h2>
      <p class="page-desc">支持大文件分片上传、断点续传、秒传、进度展示</p>
    </div>

    <!-- 上传区域 -->
    <div class="upload-area">
      <!-- 拖拽上传区域 -->
      <div 
        class="upload-drop-zone" 
        :class="{ 'is-dragover': isDragOver }"
        @click="handleSelectFile"
        @drop.prevent="handleDrop"
        @dragover.prevent="isDragOver = true"
        @dragleave.prevent="isDragOver = false"
      >
        <i class="el-icon-upload upload-icon"></i>
        <div class="upload-text">
          <div class="upload-title">点击或拖拽视频文件到此区域上传</div>
          <div class="upload-hint">支持 MP4、AVI、MOV、MKV 等格式，单个文件最大支持 10GB</div>
        </div>
        <input 
          ref="fileInput" 
          type="file" 
          accept="video/*" 
          style="display: none"
          @change="handleFileChange"
        >
      </div>

      <!-- 文件信息 -->
      <div v-if="file" class="file-info">
        <div class="info-row">
          <span class="info-label">文件名：</span>
          <span class="info-value">{{ file.name }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">文件大小：</span>
          <span class="info-value">{{ formatFileSize(file.size) }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">文件MD5：</span>
          <span class="info-value">{{ fileHash || '计算中...' }}</span>
          <el-tag v-if="fileHash" size="mini" type="success" style="margin-left: 10px">已完成</el-tag>
        </div>
        <div class="info-row">
          <span class="info-label">分片数量：</span>
          <span class="info-value">{{ chunkCount }} 个</span>
          <span style="margin-left: 20px; color: #909399; font-size: 12px">
            (每个分片 {{ formatFileSize(chunkSize) }})
          </span>
        </div>
      </div>

      <!-- 上传进度 -->
      <div v-if="uploadStatus !== 'ready'" class="upload-progress">
        <div class="progress-header">
          <span class="progress-title">上传进度</span>
          <el-tag :type="uploadStatusTagType">{{ uploadStatusText }}</el-tag>
        </div>
        
        <!-- 总进度条 -->
        <div class="progress-item">
          <div class="progress-label">总进度</div>
          <el-progress 
            :percentage="totalProgress" 
            :status="totalProgressStatus"
            :stroke-width="16"
            :text-inside="true"
          />
          <div class="progress-detail">
            <span>{{ uploadedChunks }} / {{ chunkCount }} 分片</span>
            <span>{{ formatFileSize(uploadedSize) }} / {{ formatFileSize(file?.size || 0) }}</span>
          </div>
        </div>

        <!-- 当前分片进度 -->
        <div v-if="currentChunkInfo" class="progress-item">
          <div class="progress-label">当前分片</div>
          <el-progress 
            :percentage="currentChunkProgress" 
            :stroke-width="12"
            :show-text="false"
          />
          <div class="progress-detail" style="font-size: 12px; color: #909399;">
            分片 #{{ currentChunkInfo.index + 1 }} - {{ formatFileSize(currentChunkInfo.size) }}
          </div>
        </div>

        <!-- 速度信息 -->
        <div v-if="uploadStatus === 'uploading'" class="speed-info">
          <i class="el-icon-time"></i>
          速度: {{ formatSpeed(speed) }}
          <span style="margin-left: 20px">
            预计剩余: {{ formatTime(remainingTime) }}
          </span>
        </div>
      </div>

      <!-- 控制按钮 -->
      <div v-if="file" class="control-buttons">
        <el-button 
          v-if="uploadStatus === 'ready' || uploadStatus === 'paused'"
          type="primary" 
          icon="el-icon-upload"
          :loading="uploadStatus === 'checking'"
          @click="startUpload"
          :disabled="!fileHash"
        >
          {{ uploadStatus === 'paused' ? '继续上传' : '开始上传' }}
        </el-button>
        
        <el-button 
          v-if="uploadStatus === 'uploading'"
          type="warning" 
          icon="el-icon-video-pause"
          @click="pauseUpload"
        >
          暂停上传
        </el-button>
        
        <el-button 
          v-if="uploadStatus !== 'ready' && uploadStatus !== 'completed'"
          type="info" 
          icon="el-icon-close"
          @click="cancelUpload"
        >
          取消上传
        </el-button>
        
        <el-button 
          v-if="uploadStatus === 'completed'"
          type="success" 
          icon="el-icon-check"
          disabled
        >
          上传完成
        </el-button>

        <el-popover
          placement="top"
          width="300"
          trigger="hover"
          v-if="uploadedChunks > 0 && uploadedChunks < chunkCount"
        >
          <div class="chunk-list">
            <div class="chunk-list-header">
              已上传分片 ({{ uploadedChunks }} / {{ chunkCount }})
            </div>
            <div class="chunk-list-body">
              <el-tag
                v-for="index in uploadedChunksList"
                :key="index"
                size="mini"
                type="success"
                style="margin: 2px"
              >
                #{{ index + 1 }}
              </el-tag>
            </div>
          </div>
          <el-button slot="reference" type="text" style="margin-left: 10px">
            查看已传分片 <i class="el-icon-view"></i>
          </el-button>
        </el-popover>
      </div>

      <!-- 秒传提示 -->
      <el-alert
        v-if="uploadStatus === 'completed' && isQuickUpload"
        title="文件已存在，秒传成功！"
        type="success"
        :closable="false"
        show-icon
        style="margin-top: 20px"
      />
    </div>

    <!-- 上传日志 -->
    <div v-if="logs.length > 0" class="upload-logs">
      <div class="logs-header">
        <span>上传日志</span>
        <el-button type="text" size="small" @click="clearLogs">清空</el-button>
      </div>
      <div class="logs-body">
        <div 
          v-for="(log, index) in logs" 
          :key="index" 
          class="log-item"
          :class="`log-${log.type}`"
        >
          <span class="log-time">{{ log.time }}</span>
          <span class="log-message">{{ log.message }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import SparkMD5 from 'spark-md5'

export default {
  name: 'VideoUpload',
  data() {
    return {
      // 文件相关
      file: null,
      fileHash: '',
      chunkSize: 0.5 * 1024 * 1024, // 512KB 分片大小
      chunkCount: 0,
      
      // 上传状态
      uploadStatus: 'ready', // ready, checking, uploading, paused, completed, error
      uploadedChunks: 0,
      uploadedSize: 0,
      uploadedChunksSet: new Set(), // 已上传分片索引集合
      
      // 当前分片信息
      currentChunkInfo: null,
      currentChunkProgress: 0,
      
      // 速度相关
      speed: 0,
      remainingTime: 0,
      lastUploadTime: 0,
      lastUploadedSize: 0,
      
      // UI状态
      isDragOver: false,
      logs: [],
      isQuickUpload: false,
      
      // 取消令牌
      cancelTokenSource: null
    }
  },
  computed: {
    // 总进度百分比
    totalProgress() {
      if (!this.file || this.chunkCount === 0) return 0
      return Math.floor((this.uploadedChunks / this.chunkCount) * 100)
    },
    
    // 总进度状态
    totalProgressStatus() {
      if (this.uploadStatus === 'completed') return 'success'
      if (this.uploadStatus === 'error') return 'exception'
      return undefined  // 返回 undefined 而不是空字符串，避免 Element UI 校验错误
    },
    
    // 上传状态文本
    uploadStatusText() {
      const statusMap = {
        ready: '准备就绪',
        checking: '检查中...',
        uploading: '上传中...',
        paused: '已暂停',
        completed: '上传完成',
        error: '上传失败'
      }
      return statusMap[this.uploadStatus] || '未知状态'
    },
    
    // 上传状态标签类型
    uploadStatusTagType() {
      const typeMap = {
        ready: 'info',
        checking: '',
        uploading: '',
        paused: 'warning',
        completed: 'success',
        error: 'danger'
      }
      return typeMap[this.uploadStatus] || 'info'
    },
    
    // 已上传分片列表
    uploadedChunksList() {
      return Array.from(this.uploadedChunksSet).sort((a, b) => a - b)
    }
  },
  methods: {
    // 选择文件
    handleSelectFile() {
      this.$refs.fileInput.click()
    },
    
    // 处理文件选择
    handleFileChange(e) {
      const file = e.target.files[0]
      if (file) {
        this.selectFile(file)
      }
    },
    
    // 处理拖拽
    handleDrop(e) {
      this.isDragOver = false
      const file = e.dataTransfer.files[0]
      if (file && file.type.startsWith('video/')) {
        this.selectFile(file)
      } else {
        this.$toast.warning('请选择视频文件')
      }
    },
    
    // 选择文件
    selectFile(file) {
      // 重置状态
      this.resetUpload()
      
      this.file = file
      this.chunkCount = Math.ceil(file.size / this.chunkSize)
      
      // 计算文件MD5
      this.calculateFileMD5(file)
      
      this.addLog(`选择文件: ${file.name} (${this.formatFileSize(file.size)})`, 'info')
    },
    
    // 计算文件MD5
    calculateFileMD5(file) {
      this.addLog('开始计算文件MD5...', 'info')
      
      const spark = new SparkMD5.ArrayBuffer()
      const fileReader = new FileReader()
      const chunks = Math.ceil(file.size / this.chunkSize)
      let currentChunk = 0
      
      const loadNextChunk = () => {
        const start = currentChunk * this.chunkSize
        const end = Math.min(start + this.chunkSize, file.size)
        const chunk = file.slice(start, end)
        fileReader.readAsArrayBuffer(chunk)
      }
      
      fileReader.onload = (e) => {
        spark.append(e.target.result)
        currentChunk++
        
        if (currentChunk < chunks) {
          // 继续计算下一个分片
          loadNextChunk()
          // 更新进度（可选）
          const progress = Math.floor((currentChunk / chunks) * 100)
          this.addLog(`MD5计算进度: ${progress}%`, 'info', false)
        } else {
          // 计算完成
          this.fileHash = spark.end()
          this.addLog(`MD5计算完成: ${this.fileHash}`, 'success')
          this.addLog(`文件分片数量: ${this.chunkCount}`, 'info')
        }
      }
      
      fileReader.onerror = () => {
        this.addLog('MD5计算失败', 'error')
        this.$toast.error('文件MD5计算失败')
      }
      
      loadNextChunk()
    },
    
    // 开始上传
    async startUpload() {
      if (!this.file || !this.fileHash) {
        this.$toast.warning('请先选择文件并等待MD5计算完成')
        return
      }
      
      this.uploadStatus = 'checking'
      this.addLog('开始检查上传状态...', 'info')
      
      try {
        // 1. 检查文件是否已存在（秒传）
        const checkResult = await this.checkFileExist()
        
        if (checkResult.exist) {
          // 文件已存在，秒传成功
          this.uploadStatus = 'completed'
          this.uploadedChunks = this.chunkCount
          this.uploadedSize = this.file.size
          this.isQuickUpload = true
          this.addLog('文件已存在，秒传成功！', 'success')
          this.$toast.success('文件已存在，秒传成功！')
          return
        }
        
        // 2. 获取已上传的分片列表
        if (checkResult.uploadedChunks && checkResult.uploadedChunks.length > 0) {
          this.uploadedChunksSet = new Set(checkResult.uploadedChunks)
          this.uploadedChunks = checkResult.uploadedChunks.length
          this.uploadedSize = this.uploadedChunks * this.chunkSize
          this.addLog(`发现 ${checkResult.uploadedChunks.length} 个已上传分片`, 'info')
        }
        
        // 3. 开始上传
        this.uploadStatus = 'uploading'
        this.addLog('开始上传文件分片...', 'info')
        
        // 创建取消令牌
        this.cancelTokenSource = axios.CancelToken.source()
        
        // 上传所有分片
        await this.uploadChunks()
        
      } catch (error) {
        if (axios.isCancel(error)) {
          this.addLog('上传已取消', 'warning')
          this.uploadStatus = 'paused'
        } else {
          this.addLog(`上传失败: ${error.message}`, 'error')
          this.uploadStatus = 'error'
          this.$toast.error('上传失败: ' + error.message)
        }
      }
    },
    
    // 检查文件是否已存在
    async checkFileExist() {
      try {
        const response = await axios.post('/api/upload/check', {
          fileHash: this.fileHash,
          fileName: this.file.name,
          fileSize: this.file.size
        })
        
        return response.data.data || { exist: false, uploadedChunks: [] }
      } catch (error) {
        this.addLog(`检查文件状态失败: ${error.message}`, 'error')
        throw error
      }
    },
    
    // 上传所有分片
    async uploadChunks() {
      const chunks = []
      
      // 创建所有分片
      for (let i = 0; i < this.chunkCount; i++) {
        const start = i * this.chunkSize
        const end = Math.min(start + this.chunkSize, this.file.size)
        const chunk = this.file.slice(start, end)
        
        chunks.push({
          index: i,
          start,
          end,
          size: end - start,
          blob: chunk
        })
      }
      
      // 并发上传（最多3个并发）
      const concurrency = 3
      
      for (let i = 0; i < chunks.length; i += concurrency) {
        if (this.uploadStatus !== 'uploading') break
        
        const chunkBatch = chunks.slice(i, i + concurrency)
        const uploadPromises = chunkBatch.map(chunk => this.uploadChunk(chunk))
        
        await Promise.all(uploadPromises)
        
        // 更新已上传分片数
        this.uploadedChunks = this.uploadedChunksSet.size
        this.uploadedSize = this.uploadedChunks * this.chunkSize
      }
      
      // 检查是否所有分片都已上传
      if (this.uploadedChunks >= this.chunkCount) {
        // 合并分片
        await this.mergeChunks()
      }
    },
    
    // 上传单个分片（使用流式上传 - headers传参）
    async uploadChunk(chunk) {
      // 检查该分片是否已上传
      if (this.uploadedChunksSet.has(chunk.index)) {
        this.addLog(`分片 #${chunk.index + 1} 已上传，跳过`, 'info', false)
        return
      }
      
      this.currentChunkInfo = chunk
      this.currentChunkProgress = 0
      this.lastUploadTime = Date.now()
      this.lastUploadedSize = 0
      
      try {
        // 使用流式上传：参数通过 headers 传递，文件通过 body 流式传输
        const chunkBuffer = await chunk.blob.arrayBuffer()
        const chunkHash = SparkMD5.ArrayBuffer.hash(chunkBuffer)

        await axios.post('/api/upload/chunk', chunk.blob, {
          headers: {
            'Content-Type': 'application/octet-stream',
            'X-File-Hash': this.fileHash,
            'X-Chunk-Index': chunk.index,
            'X-Chunk-Hash': chunkHash,
            'X-Total-Chunks': this.chunkCount
          },
          cancelToken: this.cancelTokenSource.token,
          onUploadProgress: (progressEvent) => {
            const progress = Math.floor((progressEvent.loaded / progressEvent.total) * 100)
            this.currentChunkProgress = progress
            
            // 计算上传速度
            this.calculateSpeed(progressEvent.loaded)
          }
        })
        
        // 上传成功
        this.uploadedChunksSet.add(chunk.index)
        this.addLog(`分片 #${chunk.index + 1} 上传成功`, 'success', false)
        
        // 重置当前分片进度
        this.currentChunkProgress = 0
        this.currentChunkInfo = null
        
      } catch (error) {
        if (axios.isCancel(error)) {
          this.addLog(`分片 #${chunk.index + 1} 上传被取消`, 'warning')
        } else {
          this.addLog(`分片 #${chunk.index + 1} 上传失败: ${error.message}`, 'error')
          throw error
        }
      }
    },
    
    // 计算上传速度
    calculateSpeed(currentLoaded) {
      const now = Date.now()
      const timeDiff = now - this.lastUploadTime
      
      if (timeDiff > 1000) { // 每秒计算一次
        const sizeDiff = currentLoaded - this.lastUploadedSize
        this.speed = (sizeDiff / timeDiff) * 1000 // 字节/秒
        
        // 计算剩余时间
        const remainingSize = this.file.size - this.uploadedSize
        this.remainingTime = remainingSize / this.speed
        
        this.lastUploadTime = now
        this.lastUploadedSize = currentLoaded
      }
    },
    
    // 合并分片
    async mergeChunks() {
      this.addLog('所有分片上传完成，开始合并...', 'info')
      this.uploadStatus = 'checking'
      
      try {
        const response = await axios.post('/api/upload/merge', {
          fileHash: this.fileHash,
          fileName: this.file.name,
          fileSize: this.file.size,
          chunkSize: this.chunkSize,
          totalChunks: this.chunkCount
        })
        
        if (response.data.code === 200) {
          this.uploadStatus = 'completed'
          this.uploadedChunks = this.chunkCount
          this.uploadedSize = this.file.size
          this.addLog('文件合并成功！', 'success')
          this.$toast.success('上传完成！')
        } else {
          throw new Error(response.data.msg || response.data.message || '合并失败')
        }
      } catch (error) {
        this.addLog(`合并失败: ${error.message}`, 'error')
        this.uploadStatus = 'error'
        throw error
      }
    },
    
    // 暂停上传
    pauseUpload() {
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel('用户暂停上传')
      }
      this.uploadStatus = 'paused'
      this.addLog('上传已暂停', 'warning')
      this.$toast.info('上传已暂停')
    },
    
    // 取消上传
    cancelUpload() {
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel('用户取消上传')
      }
      this.resetUpload()
      this.addLog('上传已取消', 'warning')
      this.$toast.info('上传已取消')
    },
    
    // 重置上传状态
    resetUpload() {
      this.uploadStatus = 'ready'
      this.uploadedChunks = 0
      this.uploadedSize = 0
      this.uploadedChunksSet.clear()
      this.currentChunkInfo = null
      this.currentChunkProgress = 0
      this.speed = 0
      this.remainingTime = 0
      this.isQuickUpload = false
      if (this.cancelTokenSource) {
        this.cancelTokenSource = null
      }
    },
    
    // 清空日志
    clearLogs() {
      this.logs = []
    },
    
    // 添加日志
    addLog(message, type = 'info', showMessage = true) {
      const time = new Date().toLocaleTimeString()
      this.logs.unshift({ time, message, type })
      
      // 限制日志数量
      if (this.logs.length > 100) {
        this.logs = this.logs.slice(0, 100)
      }
      
      if (showMessage && type === 'error') {
        console.error(`[${time}] ${message}`)
      } else if (showMessage && type === 'warning') {
        console.warn(`[${time}] ${message}`)
      } else if (showMessage) {
        console.log(`[${time}] ${message}`)
      }
    },
    
    // 格式化文件大小
    formatFileSize(size) {
      if (!size) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB', 'TB']
      let index = 0
      while (size >= 1024 && index < units.length - 1) {
        size /= 1024
        index++
      }
      return `${size.toFixed(2)} ${units[index]}`
    },
    
    // 格式化速度
    formatSpeed(speed) {
      if (!speed) return '0 B/s'
      return this.formatFileSize(speed) + '/s'
    },
    
    // 格式化时间
    formatTime(seconds) {
      if (!seconds || seconds === Infinity) return '计算中...'
      
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const secs = Math.floor(seconds % 60)
      
      if (hours > 0) {
        return `${hours}小时 ${minutes}分钟`
      } else if (minutes > 0) {
        return `${minutes}分钟 ${secs}秒`
      } else {
        return `${secs}秒`
      }
    },
    
    // 获取坐标系名称
    getCoordName(coord) {
      const names = {
        wgs84: 'WGS84 (GPS)',
        gcj02: 'GCJ02 (高德)',
        bd09: 'BD09 (百度)'
      }
      return names[coord] || coord
    }
  },
  beforeDestroy() {
    // 组件销毁时取消上传
    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel('组件销毁')
    }
  }
}
</script>

<style lang="scss" scoped>
.video-upload-container {
  /* 撑满内容区，滚动下沉到模块内部；居中限宽保留 */
  height: 100%;
  box-sizing: border-box;
  overflow: auto;
  padding: 0;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  text-align: center;
  margin-bottom: 30px;
  
  h2 {
    margin: 0 0 10px 0;
    color: #303133;
    font-size: 24px;
  }
  
  .page-desc {
    margin: 0;
    color: #909399;
    font-size: 14px;
  }
}

.upload-area {
  background: var(--surface-1);
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.upload-drop-zone {
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  padding: 60px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background: var(--surface-1);
  
  &:hover {
    border-color: #409eff;
    background: #f0f7ff;
  }
  
  &.is-dragover {
    border-color: #409eff;
    background: #e6f3ff;
    transform: scale(1.02);
  }
}

.upload-icon {
  font-size: 48px;
  color: #409eff;
  margin-bottom: 20px;
}

.upload-title {
  font-size: 18px;
  color: #303133;
  margin-bottom: 8px;
}

.upload-hint {
  font-size: 14px;
  color: #909399;
}

.file-info {
  margin-top: 20px;
  padding: 20px;
  background: var(--surface-2);
  border-radius: 8px;
  
  .info-row {
    display: flex;
    align-items: center;
    margin-bottom: 10px;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    .info-label {
      font-weight: 500;
      color: #606266;
      min-width: 80px;
      font-size: 14px;
    }
    
    .info-value {
      flex: 1;
      color: #303133;
      font-family: monospace;
      font-size: 14px;
    }
  }
}

.upload-progress {
  margin-top: 20px;
  padding: 20px;
  background: var(--surface-2);
  border-radius: 8px;
  
  .progress-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    
    .progress-title {
      font-weight: 500;
      color: #303133;
      font-size: 16px;
    }
  }
  
  .progress-item {
    margin-bottom: 20px;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    .progress-label {
      font-size: 14px;
      color: #606266;
      margin-bottom: 8px;
      font-weight: 500;
    }
    
    .progress-detail {
      display: flex;
      justify-content: space-between;
      margin-top: 8px;
      font-size: 13px;
      color: #909399;
    }
  }
  
  .speed-info {
    margin-top: 15px;
    padding: 10px;
    background: var(--surface-1);
    border-radius: 4px;
    font-size: 13px;
    color: #606266;
    text-align: center;
    
    i {
      margin-right: 5px;
      color: #409eff;
    }
  }
}

.control-buttons {
  margin-top: 20px;
  text-align: center;
  
  .el-button {
    margin: 0 10px;
    min-width: 120px;
  }
}

.chunk-list {
  max-height: 200px;
  overflow-y: auto;
  
  .chunk-list-header {
    font-weight: 500;
    margin-bottom: 10px;
    color: #303133;
    font-size: 14px;
  }
  
  .chunk-list-body {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
  }
}

.upload-logs {
  margin-top: 20px;
  background: var(--surface-1);
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  
  .logs-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 20px;
    background: var(--surface-2);
    border-bottom: 1px solid #EBEEF5;
    
    span {
      font-weight: 500;
      color: #303133;
    }
  }
  
  .logs-body {
    max-height: 300px;
    overflow-y: auto;
    padding: 10px 20px;
    background: var(--surface-1);
  }
  
  .log-item {
    display: flex;
    padding: 8px 0;
    font-size: 13px;
    border-bottom: 1px solid rgba(31, 45, 61, 0.06);
    
    &:last-child {
      border-bottom: none;
    }
    
    &.log-info {
      color: #606266;
    }
    
    &.log-success {
      color: #67c23a;
    }
    
    &.log-warning {
      color: #e6a23c;
    }
    
    &.log-error {
      color: #f56c6c;
    }
    
    .log-time {
      margin-right: 15px;
      color: #909399;
      min-width: 70px;
      font-family: monospace;
    }
    
    .log-message {
      flex: 1;
      word-break: break-all;
    }
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .video-upload-container {
    padding: 10px;
  }
  
  .upload-area {
    padding: 20px;
  }
  
  .control-buttons .el-button {
    margin: 5px;
    min-width: 100px;
  }
}
</style>
